/**
 * Structured conversation summary for agent handoff previews.
 *
 * Fixed four-section shape, in this order: Initial questions asked, Summary,
 * Analysis so far, Last step taken. The server's own copies of this summary
 * (_simple_conversation_summary and the /summarize-conversation prompt in
 * server/app/api/v1/copilot_sessions.py) render the SAME four sections -- an
 * engineer must not get a different shape depending on which path produced the
 * text.
 *
 * Renders as markdown sections consumed by formatBotMessage.
 */

function normalizeText(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

function collectByRole(messages, roles) {
  const set = new Set(roles.map((r) => r.toLowerCase()));
  return messages.filter((m) => set.has(String(m.sender || "").toLowerCase()));
}

/** Only the AI-assisted phase — stop at the first live-agent message. */
function messagesBeforeLiveAgent(messages) {
  const idx = messages.findIndex(
    (m) => String(m.sender || "").toLowerCase() === "agent",
  );
  return idx === -1 ? messages : messages.slice(0, idx);
}

const ASSISTANT_ROLES = new Set(["bot", "assistant", "copilot"]);
const CUSTOMER_ROLES = new Set(["user", "customer"]);
const ACTION_RE = /\b(run|ran|check|checked|try|tried|verify|verified|confirm|confirmed|review|reviewed|inspect|inspected|restart|restarted|reboot|rebooted|collect|collected|gather|gathered|test|tested|open|opened|disable|disabled|enable|enabled|clear|cleared|remove|removed|update|updated|install|installed|execute|executed|compare|compared|capture|captured)\b/i;
const RESULT_RE = /\b(tried|ran|checked|tested|restarted|rebooted|confirmed|verified|result|output|showed|shows|worked|working|failed|fails|error|still|same|resolved|fixed|unable|cannot|can't|did|didn't|does|doesn't|yes|no)\b/i;
// How far into the chat still counts as "initial".
const INITIAL_WINDOW = 6;
const HANDOFF_NOISE_RE = /\b(escalat|live (support )?agent|support engineer|hand[\s-]?off|join shortly|please hold)\b/i;

function cleanTranscriptText(text) {
  return normalizeText(
    String(text || "")
      .replace(/```(?:\w+)?/g, " ")
      .replace(/[*_#>`]/g, " ")
      .replace(/^\s*[-\d.)]+\s*/gm, ""),
  );
}

function sentences(text) {
  const clean = cleanTranscriptText(text);
  if (!clean) return [];
  const parts = clean.match(/[^.!?]+(?:[.!?]+|$)/g) || [clean];
  return parts.map((part) => normalizeText(part)).filter(Boolean);
}

function firstUsefulSentence(text, predicate = () => true) {
  return sentences(text).find((sentence) => predicate(sentence)) || "";
}

function usefulExcerpt(text, predicate = () => true, limit = 2) {
  return sentences(text).filter(predicate).slice(0, limit).join(" ");
}

function relatedCustomerResult(messages, assistantIndex) {
  for (let i = assistantIndex + 1; i < messages.length; i += 1) {
    const role = String(messages[i]?.sender || "").toLowerCase();
    if (ASSISTANT_ROLES.has(role)) break;
    if (!CUSTOMER_ROLES.has(role)) continue;
    const result = usefulExcerpt(messages[i]?.text, (sentence) => RESULT_RE.test(sentence));
    if (result) return result;
  }
  return "";
}

function extractConcreteActions(messages) {
  const actions = [];
  const seen = new Set();

  messages.forEach((message, index) => {
    const role = String(message?.sender || "").toLowerCase();
    if (!ASSISTANT_ROLES.has(role)) return;

    const action = usefulExcerpt(
      message?.text,
      (sentence) => ACTION_RE.test(sentence) && !HANDOFF_NOISE_RE.test(sentence),
    );
    if (!action) return;

    const key = action.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (seen.has(key)) return;
    seen.add(key);

    const result = relatedCustomerResult(messages, index);
    actions.push(result ? `${action} Customer result: ${result}` : action);
  });

  // The most recent actions are the most useful at handoff.
  return actions.slice(-3);
}

function currentHandoffState(messages) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const role = String(messages[i]?.sender || "").toLowerCase();
    if (!ASSISTANT_ROLES.has(role)) continue;

    const assistantStep = firstUsefulSentence(
      messages[i]?.text,
      (sentence) => !HANDOFF_NOISE_RE.test(sentence) && (sentence.includes("?") || ACTION_RE.test(sentence)),
    );
    if (!assistantStep) continue;

    let customerReply = "";
    for (let j = i + 1; j < messages.length; j += 1) {
      const replyRole = String(messages[j]?.sender || "").toLowerCase();
      if (!CUSTOMER_ROLES.has(replyRole)) continue;
      customerReply = usefulExcerpt(messages[j]?.text);
    }
    return customerReply
      ? `AI: ${assistantStep} Customer: ${customerReply}`
      : `Awaiting customer response to: ${assistantStep}`;
  }

  const latestCustomer = [...messages]
    .reverse()
    .find((m) => CUSTOMER_ROLES.has(String(m?.sender || "").toLowerCase()));
  const customerText = firstUsefulSentence(latestCustomer?.text);
  return customerText
    ? `Customer's latest update: ${customerText}`
    : "No active troubleshooting step was recorded before escalation.";
}

/** The opening asks, in order: what the customer came in with, then the first
 *  things the AI wanted to know. Scanned over the OPENING of the chat only --
 *  a question asked on turn 20 is analysis, not an initial question, and it
 *  already shows up under "Analysis so far" / "Last step taken". */
function extractInitialQuestions(messages, customerName) {
  const opening = messages.slice(0, INITIAL_WINDOW);
  const asks = [];
  const seen = new Set();

  opening.forEach((message) => {
    const role = String(message?.sender || "").toLowerCase();
    const isCustomer = CUSTOMER_ROLES.has(role);
    const isAssistant = ASSISTANT_ROLES.has(role);
    if (!isCustomer && !isAssistant) return;

    sentences(message?.text)
      .filter((sentence) => sentence.includes("?") && !HANDOFF_NOISE_RE.test(sentence))
      .forEach((sentence) => {
        const key = sentence.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        asks.push(`${isCustomer ? customerName : "AI"}: ${sentence}`);
      });
  });

  if (asks.length) return asks.slice(0, 3);

  // Nobody phrased anything as a question -- the customer still opened with an
  // ask, so report that rather than an empty section.
  const firstCustomer = opening.find((m) => CUSTOMER_ROLES.has(String(m?.sender || "").toLowerCase()));
  const opened = firstUsefulSentence(firstCustomer?.text);
  return opened ? [`${customerName}: ${opened}`] : [];
}

function bulletSection(items, fallback) {
  if (!items.length) return fallback;
  return items.map((item) => `- ${item}`).join("\n");
}

/** A speaker block: the bold label, then its bullets. Omitted entirely when
 *  that side did nothing — an empty block reads as a fact ("the user said
 *  nothing") when it only means this half of the section is unused. Mirrors the
 *  shape the model is asked for, so a fallback summary is laid out the same way
 *  as a written one and the engineer never has to re-learn the page. */
function speakerBlock(label, items) {
  if (!items.length) return "";
  return [`**${label}**`, bulletSection(items, "")].join("\n");
}

function joinBlocks(blocks, fallback) {
  const present = blocks.filter(Boolean);
  return present.length ? present.join("\n\n") : fallback;
}

/**
 * @param {Array<{sender?: string, text?: string}>} messages
 * @param {{ customerName?: string }} [options]
 * @returns {string}
 */
export function buildConversationSummary(messages = [], options = {}) {
  const { customerName = "Customer" } = options;
  const chat = messagesBeforeLiveAgent(
    (messages || []).filter((m) => String(m.text || "").trim()),
  );
  if (!chat.length) {
    return "No conversation to summarize yet. Messages will appear here once the customer and assistant have exchanged a few turns.";
  }

  const customerMsgs = collectByRole(chat, ["user", "customer"]);
  const firstCustomerText = normalizeText(
    (customerMsgs[0] || chat.find((m) => ["user", "customer"].includes(String(m.sender || "").toLowerCase())) || chat[0])?.text,
  );

  const summaryText = firstCustomerText
    ? `${customerName}: ${firstCustomerText}`
    : `${customerName} opened a support conversation about a technical issue.`;

  const initialQuestions = extractInitialQuestions(chat, customerName);
  const steps = extractConcreteActions(chat);
  const lastStep = currentHandoffState(chat);

  // extractInitialQuestions tags each ask with its speaker ("<name>: ..." or
  // "AI: ..."), which is exactly the split the sections need — so the prefix is
  // consumed into the block label rather than repeated on every bullet.
  const customerAsks = initialQuestions
    .filter((q) => q.startsWith(`${customerName}:`))
    .map((q) => q.slice(customerName.length + 1).trim());
  const assistantAsks = initialQuestions
    .filter((q) => q.startsWith("AI:"))
    .map((q) => q.slice(3).trim());

  const sections = [
    "**Initial questions asked**",
    joinBlocks(
      [speakerBlock("User", customerAsks), speakerBlock("Copilot", assistantAsks)],
      "- No question was asked before escalation.",
    ),
    "",
    "**Summary**",
    // Everything this builder can state about the case comes from what the
    // customer reported — the regex pass has no read on what the assistant
    // concluded, so there is no Copilot block to write honestly here.
    speakerBlock("User", [summaryText]),
    "",
    "**Analysis so far**",
    // extractConcreteActions walks assistant messages only.
    joinBlocks(
      [speakerBlock("Copilot", steps)],
      "**Copilot**\n- No concrete troubleshooting actions were recorded before escalation.",
    ),
    "",
    "**Last step taken**",
    joinBlocks(
      [speakerBlock("Copilot", lastStep ? [lastStep] : [])],
      "**Copilot**\n- No assistant guidance recorded before escalation.",
    ),
  ];

  return sections.join("\n");
}

export function normalizeSummaryMessages(messages = []) {
  return (messages || [])
    .filter((m) => {
      if (!m.text || m.type === "status" || m.isStreaming) return false;
      return true;
    })
    .map((m) => {
      const sender = String(m.sender || "user").toLowerCase();
      if (sender === "bot") return { sender: "assistant", text: m.text };
      return { sender, text: m.text };
    });
}
